import { useEffect, useRef } from "react";
import Stack from "@mui/material/Stack";
import { useTheme } from "@mui/material";

import { IgPaper } from "@intelligems/ig-theme";

import type { AgenticLogicSurface as AgenticLogicServingSurface } from "@intelligems/ig-types";

import { useSelector } from "@src/store";
import { getIsAtlasCreationSessionActive } from "@src/store/slices/global";

import { CreateRuleTab } from "../create/CreateRuleTab";
import { CreateRuleDesigningState } from "../create/CreateRuleDesigningState";
import { AgenticLogicSurfaceProvider } from "../surface/agenticLogicSurfaceProvider";
import { useSurfaceNavigate } from "../surface/useSurfaceNavigate";
import {
  useEmbeddedAgenticLogicSurface,
  useEmbeddedAgenticLogicView,
} from "../surface/useEmbeddedAgenticLogicSurface";
import { AgenticLogicRulePage } from "../workspace/AgenticLogicRulePage";
import {
  INITIAL_TAKEOVER_BINDING,
  nextTakeoverBinding,
} from "./takeoverBinding";
import { TakeoverFrame } from "./TakeoverFrame";

export interface AgenticLogicTakeoverProps {
  /** The host's name, for the frame's crumb — e.g. "Upsell builder". */
  title: string;
  /** The back button's copy — e.g. "Back to upsell settings". */
  backLabel: string;
  /**
   * Where the host's placement renders. Required: the author came here FROM a
   * placement, and everything offered before they type — the rule ideas above
   * the composer chief among them — has to describe a rule that placement can
   * actually run.
   */
  placementSurface: AgenticLogicServingSurface;
  /**
   * Called with the rule this takeover CREATED, once it exists. Editing an
   * existing rule never calls it: nothing about the host's binding changed.
   */
  onRuleCreated: (ruleId: string) => void;
}

/**
 * The rule builder, rendered in place of a host's own content.
 *
 * Same three views as the standalone surface and the same components behind
 * them — they read everything that differs from the surface descriptor, and an
 * embedded one exits to its host instead of to a rules list. What is NOT the
 * same is the frame: there is no route to leave and no breadcrumb bar to fill,
 * because the host is a dialog.
 */
export const AgenticLogicTakeover = ({
  title,
  backLabel,
  placementSurface,
  onRuleCreated,
}: AgenticLogicTakeoverProps) => {
  const surface = useEmbeddedAgenticLogicSurface({
    title,
    backLabel,
    placementSurface,
  });
  const view = useEmbeddedAgenticLogicView();
  const navigate = useSurfaceNavigate();
  const isAtlasSessionActive = useSelector(getIsAtlasCreationSessionActive);
  const theme = useTheme();

  // Which entry this takeover was opened by. A build hands the author from the
  // creation view to the rule it produced, and THAT transition — not the mere
  // presence of a rule id — is what binds the new rule to the host's
  // placement. Opening straight onto a rule is the Edit affordance, which
  // changes no binding.
  // The decision itself is pure — see `nextTakeoverBinding`.
  const binding = useRef(INITIAL_TAKEOVER_BINDING);
  const viewKind = view.kind;
  const ruleId = view.kind === "rule" ? view.ruleId : null;
  useEffect(() => {
    const { state, bind } = nextTakeoverBinding(
      binding.current,
      ruleId === null
        ? { kind: viewKind as "list" | "new" }
        : { kind: "rule", ruleId }
    );
    binding.current = state;
    if (bind) onRuleCreated(bind);
  }, [onRuleCreated, ruleId, viewKind]);

  if (view.kind === "list") return null;

  return (
    <AgenticLogicSurfaceProvider surface={surface}>
      <Stack sx={{ px: 4, py: 3, flex: 1, minHeight: 0 }}>
        {view.kind === "new" ? (
          // The workspace below brings its own frame; these views do not.
          <TakeoverFrame
            backLabel={backLabel}
            onBack={() => navigate(surface.exitPath)}
          >
            {isAtlasSessionActive ? (
              <CreateRuleDesigningState />
            ) : (
              <CreateRuleTab presentation={{ kind: "embedded" }} />
            )}
          </TakeoverFrame>
        ) : (
          // The workspace's frame is `embedded`: it expects its host to supply
          // the card, as `IgRootPageWrapper` does on the standalone surface.
          // Here the host is a dialog, so the card is ours — the same one
          // `TakeoverFrame` draws around the creation views.
          <IgPaper
            sx={{
              borderColor: theme.igBorders.core.default,
              p: 0,
              borderRadius: 1,
              overflow: "hidden",
              display: "flex",
              flex: 1,
              minHeight: 0,
            }}
          >
            {/* Keyed so switching rules rebuilds the workspace rather than
                feeding a new rule to the previous one's editor state. */}
            <AgenticLogicRulePage key={view.ruleId} ruleId={view.ruleId} />
          </IgPaper>
        )}
      </Stack>
    </AgenticLogicSurfaceProvider>
  );
};
